import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Divider,
  Grid,
  GridRow,
  GridColumn,
  Header,
  Icon,
  Dropdown,
  Input,
  Message,
  Form,
  FormGroup,
  FormField,
  Select,
  Popup,
  Checkbox,
  Label
} from 'semantic-ui-react';
import { ActionButton, LoadingWrapper } from "../common";
import {
  canEditAll,
  getCSRFToken,
  fetchWithAuth,
  isAssociateManagerOf,
  isManager,
} from '../auth';
import { getMemberAssets } from '../common/apiClient';
import { DERIVED_SUPERVISOR_TEXT, isSupervisorDerived } from '../teams/teamMembership';
import { flattenMessages } from '../teams/teamMembershipService';
import { useTeamLeaders } from '../teams/useTeamLeaders';
import {
  MEMBER_COMPLIANCE_STATUS_OPTIONS,
  MEMBER_PCARD_STATUS_OPTIONS,
  MEMBER_DEVICE_TYPE_OPTIONS,
  formatAssignedDeviceTypes,
  formatComplianceStatus,
} from '../../constants/memberProfile';
import { formatPhoneNumber, getRawPhoneNumber, safeFormatPhoneNumber, validatePhoneNumber } from '../../utils/phoneNumber';
import { ASSOCIATE_ROLES } from "../../constants/common"
import { MemberStatuses } from "./Members";
import { validEmail } from "../common/helpers";
import '../../css/common.css'
import './Members.css'

// The API's `teams` includes secondary memberships flagged with `isSecondary`.
const splitMemberTeams = ({ teams = [], secondaryTeams }) => ({
  primaryTeams: teams.filter((team) => !team.isSecondary),
  secondaryTeams: secondaryTeams ?? teams.filter((team) => team.isSecondary).map(({ id, name }) => ({ id, name })),
})

export const MemberDetails = () => {
  const { id } = useParams();
  const memberDetailsEndpoint = process.env.REACT_APP_ICC_TOOL_API_HOST + 'members/' + id + '/'
  const membersEndpoint = process.env.REACT_APP_ICC_TOOL_API_HOST + 'members/'
  const memberPositionsEndpoint = process.env.REACT_APP_ICC_TOOL_API_HOST + 'members/positions/'
  const rolesEndpoint = process.env.REACT_APP_ICC_TOOL_API_HOST + 'roles/'

  const [formDetails, setFormDetails] = useState({})
  const [memberDetails, setMemberDetails] = useState({});
  const [allMembers, setAllMembers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availablePositions, setAvailablePositions] = useState([]);
  const [allMembersError, setAllMembersError] = useState('')
  const [memberDetailsError, setMemberDetailsError] = useState('')
  const [rolesError, setRolesError] = useState('')
  const [submissionError, setSubmissionError] = useState('')
  const [submissionSuccess, setSubmissionSuccess] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [membersLoaded, setMembersLoaded] = useState(false)
  const [assignedAssets, setAssignedAssets] = useState([])
  const [assetsLoaded, setAssetsLoaded] = useState(false)
  const [secondaryTeams, setSecondaryTeams] = useState([])
  const [supervisorInfo, setSupervisorInfo] = useState(null)

  // Managers can edit all member they can list; the backend verifies team scope.
  const canEditMemberDetails = canEditAll() || isAssociateManagerOf(memberDetails.supervisor, memberDetails.teams) || isManager();
  const visibleSecondaryTeams = secondaryTeams.filter(
    (secondary) => !memberDetails.teams?.some((primary) => primary.id === secondary.id)
  )
  const teamLeaders = useTeamLeaders((formDetails.teams || []).map(team => team.id))
  const supervisorDerived = teamLeaders.loaded && !teamLeaders.error
    && isSupervisorDerived(formDetails.role, teamLeaders.primaryIds)


  const multipleTeamsAllowed = () => {
    return formDetails?.role === 'Associate Manager' || formDetails?.role === 'Manager'
  }

  async function getMemberDetail() {
    setLoaded(false)
    try {
      const memberDetailsResp = await fetchWithAuth(memberDetailsEndpoint, {
        headers: { 'X-CSRFToken': getCSRFToken() }
      });
      if (memberDetailsResp.ok) {
        const memberDetailsRespJson = await memberDetailsResp.json();
        const assignedDevices = memberDetailsRespJson.assignedDevices || []
        const { primaryTeams, secondaryTeams: memberSecondaryTeams } = splitMemberTeams(memberDetailsRespJson)
        const newMemberDetails = {
          'userId': memberDetailsRespJson.userId,
          'name': memberDetailsRespJson.name,
          'supervisor': memberDetailsRespJson.supervisor?.userId,
          'jobTitle': memberDetailsRespJson.jobTitle,
          'role': memberDetailsRespJson.role,
          'position': memberDetailsRespJson.position,
          'status': memberDetailsRespJson.status?.toLowerCase(),
          'productionSheetApprovers': memberDetailsRespJson.productionSheetApprovers,
          'teams': primaryTeams,
          'outOfOfficeStartDate': memberDetailsRespJson.outOfOfficeStartDate ? new Date(memberDetailsRespJson.outOfOfficeStartDate) : null,
          'outOfOfficeEndDate': memberDetailsRespJson.outOfOfficeEndDate ? new Date(memberDetailsRespJson.outOfOfficeEndDate) : null,
          'loginAccess': memberDetailsRespJson.loginAccess,
          'cdlStatus': memberDetailsRespJson.cdlStatus || 'unknown',
          'dotStatus': memberDetailsRespJson.dotStatus || 'unknown',
          'pcardStatus': memberDetailsRespJson.pcardStatus || 'unknown',
          'contactPhone': safeFormatPhoneNumber(memberDetailsRespJson.contactPhone || ''),
          'associateNumber': memberDetailsRespJson.associateNumber || '',
          'hireDate': memberDetailsRespJson.hireDate || null,
          'assignedDevices': assignedDevices,
        }
        setMemberDetails(newMemberDetails)
        setFormDetails(newMemberDetails)
        setSupervisorInfo(memberDetailsRespJson.supervisor || null)
        setSecondaryTeams(memberSecondaryTeams)
      } else if (memberDetailsResp.status === 404) {
        setMemberDetailsError("Could not find this member.")
      } else {
        setMemberDetailsError("Unable to retrieve member details. Please refresh and try again.")
      }
    } catch {
      setMemberDetailsError("Error while trying to retrieve member details. Please refresh and try again.")
    }
    setLoaded(true)
  }

  async function getMembers() {
    setMembersLoaded(false)
    try {
      const membersResp = await fetchWithAuth(membersEndpoint, {
        headers: { 'X-CSRFToken': getCSRFToken() }
      });
      if (membersResp.ok) {
        const membersRespJson = await membersResp.json()
        setAllMembers(membersRespJson)
      } else {
        setAllMembersError("Unable to retrieve list of members. Please refresh and try again.")
      }
    } catch {
      setAllMembersError("Error while trying to retrieve list of members. Please refresh and try again.")
    }
    setMembersLoaded(true)
  }

  async function getAssignedAssets() {
    setAssetsLoaded(false)
    try {
      const memberAssets = await getMemberAssets(id)
      setAssignedAssets(memberAssets)
    } catch (error) {
      console.error("Error fetching assigned assets:", error)
      setAssignedAssets([])
    }
    setAssetsLoaded(true)
  }

  useEffect(() => {
    setMemberDetailsError("")
    getMemberDetail()
    getMembers()
    getAssignedAssets()
  }, [id])

  useEffect(() => {
    async function getAvailableRoles() {
      const options = [];
      try {
        const resp = await fetchWithAuth(rolesEndpoint, {
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        });
        if (resp.ok) {
          const respJson = await resp.json()
          respJson.forEach((role) => {
            const option = {
              key: role.key,
              value: role.value,
              text: role.value
            };
            options.push(option)
          })
          setAvailableRoles(options)
        } else {
          setRolesError("Unable to retrieve available roles. Please refresh and try again.")
        }
      } catch {
        setRolesError("Error while trying to retrieve available roles. Please try again.")
      }
    }

    async function getAvailablePositions() {
      const options = [];
      try {
        const resp = await fetchWithAuth(memberPositionsEndpoint, {
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        });
        if (resp.ok) {
          const respJson = await resp.json()
          respJson.forEach((role) => {
            const option = {
              key: role.id,
              value: role.id,
              text: role.name
            };
            options.push(option)
          })
          setAvailablePositions(options)
        } else {
          setRolesError("Unable to retrieve available positions. Please refresh and try again.")
        }
      } catch {
        setRolesError("Error while trying to retrieve available positions. Please try again.")
      }
    }

    getAvailableRoles()
    getAvailablePositions()
  }, [])

  useEffect(() => {
    if (formDetails.role !== 'Manager' && formDetails.role !== 'Associate Manager' && formDetails.teams?.length > 1) {
      setMemberDetailsError('An associate may not be on more than one team. Please update them to a manager and remove desired team.')
    } else {
      setMemberDetailsError('')
    }
  }, [formDetails])

  /* Handlers for inputs and buttons */

  const saveChangesHandler = () => {
    if (!isFormValid()) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    clearFormErrors()
    saveChanges()
  };

  const buildSavePayload = () => {
    const assignedDevices = formDetails.assignedDevices || []
    const payload = {
      ...formDetails,
      assignedDevices,
      contactPhone: getRawPhoneNumber(formDetails.contactPhone) || formDetails.contactPhone,
    }
    delete payload.teams
    return payload
  }

  const isFormValid = () => {
    if (canEditMemberDetails && formDetails.contactPhone && !validatePhoneNumber(formDetails.contactPhone)) {
      setMemberDetailsError("Please enter a valid contact phone number.")
      return false
    }
    if (formDetails.loginAccess?.enabled === true) {
      if (formDetails.loginAccess.email === null || formDetails.loginAccess.email === '') {
        setMemberDetailsError("An email address is required to enable login access for this user.")
        return false
      }
      if (!validEmail(formDetails.loginAccess.email)) {
        setMemberDetailsError("Please enter a valid email address.");
        return false
      }
    }
    return true
  }


  async function saveChanges() {
    setLoaded(false)

    try {
      const resp = await fetchWithAuth(memberDetailsEndpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify(buildSavePayload()),
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (resp.ok) {
        setSubmissionError('')
        setSubmissionSuccess('Saved successfully!')
        // The backend may reassign the supervisor; reload rather than trusting the PATCH body.
        await Promise.all([getMemberDetail(), getMembers(), getAssignedAssets()])
      } else {
        setSubmissionSuccess('')
        let errorDetail = `Server returned ${resp.status}.`
        const contentType = resp.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          try {
            const messages = flattenMessages(await resp.json()).trim()
            if (messages) {
              errorDetail = messages
            }
          } catch (error) {
            console.error('Error parsing save error response:', error)
          }
        }
        setSubmissionError('Unable to save changes. ' + errorDetail)
      }
    } catch (error) {
      console.error('Error saving member profile:', error)
      setSubmissionError('Unable to save changes. Please click "Save changes" or refresh to try again.')
    }
    setLoaded(true)
  }

  const cancelSaveChangesHandler = () => {
    clearFormErrors()
    setFormDetails(memberDetails);
  };

  const inputChangeHandler = (event, data) => {
    setSubmissionSuccess("")
    const inputName = data.name
    if (!canEditMemberDetails) {
      return
    }
    if (event.hasOwnProperty('preventDefault')) {
      event.preventDefault();
    }
    clearFormErrors()
    const value = data.name === 'contactPhone' ? formatPhoneNumber(data.value) : data.value

    // const formDetailsClone = structuredClone(formDetails)
    switch (inputName) {
      case 'name':
        formDetails.name = value
        break;
      case 'role':
        if (ASSOCIATE_ROLES.includes(value.toLowerCase()) && formDetails.teams.length > 1) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          setMemberDetailsError("An associate may not be on more than one team.")
          return
        } else {
          setMemberDetailsError("")
        }
        formDetails.role = value
        break;
      case 'position':
        formDetails.position = value
      case 'status':
        formDetails.status = value
        break;
      case 'jobTitle':
        formDetails.jobTitle = value
        break;
      case 'supervisor':
        formDetails.supervisor = value
        break;
      case 'outOfOfficeStartDate':
        formDetails.outOfOfficeStartDate = value
        break;
      case 'outOfOfficeEndDate':
        formDetails.outOfOfficeEndDate = value
        break;
      case 'loginEmail':
        formDetails.loginAccess.email = value;
        break;
      case 'loginEnabled':
        formDetails.loginAccess.enabled = data.checked
        break;
      case 'cdlStatus':
      case 'dotStatus':
      case 'pcardStatus':
      case 'contactPhone':
      case 'assignedDevices':
      case 'associateNumber':
      case 'hireDate':
        setFormDetails({ ...formDetails, [inputName]: value })
        return
      default:
        break
    }

    setFormDetails({
      ...formDetails
    })

  }

  const clearFormErrors = () => {
    setMemberDetailsError('')
    setRolesError('')
    setSubmissionError('')
  }


  return (
    <LoadingWrapper loading={!loaded} text="Loading member">
      <div align="center">

        <div style={{ width: "35%", minWidth: "25rem" }}>

          {memberDetailsError !== '' &&
            <Message negative>{memberDetailsError}</Message>
          }

          {allMembersError !== '' &&
            <Message negative>{allMembersError}</Message>
          }

          {rolesError !== '' &&
            <Message negative>{rolesError}</Message>
          }

          {submissionError !== '' &&
            <Message negative>{submissionError}</Message>
          }

          {submissionSuccess !== '' &&
            <Message positive>{submissionSuccess}</Message>
          }

        </div>

        <Divider horizontal>
          <Header as='h4'>
            <Icon name='user' />
            Member Details
          </Header>
        </Divider>

        <div className="member-profile-layout">
          <Form>
            <FormGroup>
              <FormField
                className="icct-disabled-input"
                style={{ marginBottom: "1rem" }}
                label="User ID"
                value={formDetails.userId || ''}
                name="userId"
                key="userId"
                onChange={inputChangeHandler}
                readOnly={!canEditAll()}
                disabled={!canEditAll()}
                control={Input}
                width={4}
              />
              <FormField
                style={{ marginBottom: "1rem" }}
                label="Name"
                value={formDetails.name || ''}
                name="name"
                key="name"
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
                control={Input}
                width={4}
              />
              <FormField
                style={{ marginBottom: "1rem" }}
                label="Job Title"
                value={formDetails.jobTitle || ''}
                name="jobTitle"
                key="jobTitle"
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
                control={Input}
                width={4}
              />
              <FormField
                style={{ marginBottom: "1rem",  minWidth: "100%" }}
                label="Role"
                value={formDetails.role || ''}
                name="role"
                key="role"
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
                control={Select}
                options={availableRoles}
                width={4}
              />
              {formDetails.role === 'Associate' && <FormField
                style={{ marginBottom: "1rem", minWidth: "100%" }}
                label="Position"
                value={formDetails.position || ''}
                name="position"
                key="position"
                onChange={inputChangeHandler}
                readOnly={!canEditAll()}
                control={Select}
                options={availablePositions}
                clearable
                width={4}
              />}
            </FormGroup>
            <div className='divider-bottom' />
            <FormGroup>
              <FormField>
                <label>Enable/Disable member login access</label>
                <Checkbox
                  name="loginEnabled"
                  key="loginEnabled"
                  toggle
                  readOnly={!canEditAll()}
                  disabled={!canEditAll()}
                  checked={formDetails.loginAccess?.enabled ?? false}
                  onChange={inputChangeHandler}
                />
              </FormField>
            </FormGroup>
            {formDetails.loginAccess?.enabled &&
              <FormGroup>
                <FormField
                  style={{ marginBottom: "1rem" }}
                  required={true}
                  label="Email"
                  value={formDetails.loginAccess?.email ?? ''}
                  name="loginEmail"
                  key="loginEmail"
                  onChange={inputChangeHandler}
                  readOnly={!canEditAll()}
                  disabled={!canEditAll()}
                  control={Input}
                  width={6}
                />
              </FormGroup>
            }
            <div className='divider-bottom' />
            <FormGroup>
              {supervisorDerived ? <FormField
                style={{ marginBottom: "1rem" }}
                label={
                  (<label>
                    Supervisor {supervisorInfo?.userId ?
                      <Link
                        style={{ paddingLeft: "0.3rem" }}
                        to={`/members/${supervisorInfo.userId}`}>
                        (View supervisor)
                      </Link> : ""}
                  </label>)
                }
                value={supervisorInfo?.name || 'Not assigned yet'}
                name="supervisor"
                key="supervisor-derived"
                readOnly
                control={Input}
                className="icct-disabled-input"
                width={8}
                fluid
                aria-describedby="derived-supervisor-help"
              /> : <FormField
                style={{ marginBottom: "1rem" }}
                label={
                  (<label>
                    Supervisor {formDetails.supervisor ?
                      <Link
                        style={{ paddingLeft: "0.3rem" }}
                        to={`/members/${formDetails.supervisor}`}>
                        (View supervisor)
                      </Link> : ""}
                  </label>)
                }
                options={allMembers.filter(member => {
                  if (teamLeaders.secondaryIds.includes(member.userId)) return false
                  return member.role === 'Director' || member.role === 'VP' || (
                    (member.role === 'Manager' || member.role === 'Associate Manager') &&
                    teamLeaders.primaryIds.includes(member.userId)
                  )
                }).map(member => {
                  return {
                    key: member.userId,
                    value: member.userId,
                    text: member.name
                  }
                })
                }
                value={formDetails.supervisor || ''}
                name="supervisor"
                key="supervisor"
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails || !teamLeaders.loaded || Boolean(teamLeaders.error)}
                control={Select}
                loading={!membersLoaded || !teamLeaders.loaded}
                error={teamLeaders.error ? { content: teamLeaders.error } : false}
              />}
              <FormField
                label={() => <label>Status<Popup
                  trigger={<Icon className="info circle" style={{ paddingLeft: "0.3rem" }} />}
                  on="hover"
                  content={"Automatically filled in from the member's schedule. Update their schedule to change their status."} /></ label>}
                value={formDetails.status}
                name="status"
                key="status"
                readOnly={!canEditAll()}
                disabled={!canEditAll()}
                control={Select}
                options={MemberStatuses}
                className="icct-disabled-selection"
              />
              {!multipleTeamsAllowed() && <FormField
                label="Team"
                value={formDetails.teams?.[0]?.name || ''}
                readOnly
                control={Input}
              />}
            </FormGroup>
            {supervisorDerived && <p id="derived-supervisor-help" style={{ marginTop: "-0.5rem", color: "#555" }}>
              {DERIVED_SUPERVISOR_TEXT}
            </p>}
            <div className='divider-bottom' />
          </Form>
        </div>

        <>
          <Divider horizontal>
            <Header as='h4'>
              <Icon name='id card' />
              Associate Profile
            </Header>
          </Divider>

        <div className="member-profile-layout">
          <Form>
            <FormGroup widths="equal">
              <FormField
                label="CDL Status"
                name="cdlStatus"
                control={!canEditMemberDetails ? Input : Select}
                value={!canEditMemberDetails ? formatComplianceStatus(formDetails.cdlStatus) : (formDetails.cdlStatus || 'unknown')}
                options={MEMBER_COMPLIANCE_STATUS_OPTIONS}
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
              />
              <FormField
                label="DOT Status"
                name="dotStatus"
                control={!canEditMemberDetails ? Input : Select}
                value={!canEditMemberDetails ? formatComplianceStatus(formDetails.dotStatus) : (formDetails.dotStatus || 'unknown')}
                options={MEMBER_COMPLIANCE_STATUS_OPTIONS}
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
              />
              <FormField
                label="P-card Status"
                name="pcardStatus"
                control={!canEditMemberDetails ? Input : Select}
                value={!canEditMemberDetails ? (MEMBER_PCARD_STATUS_OPTIONS.find((o) => o.value === formDetails.pcardStatus)?.text || formDetails.pcardStatus) : (formDetails.pcardStatus || 'unknown')}
                options={MEMBER_PCARD_STATUS_OPTIONS}
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
              />
            </FormGroup>
            <FormGroup widths="equal">
              <FormField
                label="Contact Phone"
                name="contactPhone"
                control={Input}
                value={formDetails.contactPhone || ''}
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
              />
              <FormField
                label="Associate Number"
                name="associateNumber"
                control={Input}
                value={formDetails.associateNumber || ''}
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
              />
              <FormField
                label="Hire Date"
                name="hireDate"
                control={Input}
                type="date"
                value={formDetails.hireDate || null}
                onChange={inputChangeHandler}
                readOnly={!canEditMemberDetails}
                disabled={!canEditMemberDetails}
              />
            </FormGroup>
          </Form>
        </div>

          <Divider horizontal>
            <Header as='h4'>
              <Icon name='mobile alternate' />
              Assigned Devices
            </Header>
          </Divider>

        <div className="member-profile-section member-profile-layout">
          <Form>
            <FormField
              label="Device types"
              name="assignedDevices"
              control={!canEditMemberDetails ? Input : Dropdown}
              fluid
              multiple={canEditMemberDetails}
              selection={canEditMemberDetails}
              options={MEMBER_DEVICE_TYPE_OPTIONS}
              value={
                !canEditMemberDetails
                  ? formatAssignedDeviceTypes(formDetails.assignedDevices)
                  : (formDetails.assignedDevices || [])
              }
              onChange={inputChangeHandler}
              readOnly={!canEditMemberDetails}
              disabled={!canEditMemberDetails}
              placeholder={!canEditMemberDetails ? '' : 'Select device types...'}
            />
          </Form>
        </div>
        </>

        <Grid>

          <Divider style={{ width: '100%' }} horizontal>
            <Header as='h4'>
              <Icon name='briefcase' />
              Assigned Assets
            </Header>
          </Divider>

          {assetsLoaded && assignedAssets.length > 0 ? (
            assignedAssets.map((asset, i) => (
              <GridRow columns={1} key={i}>
                <GridColumn textAlign='center' verticalAlign={'middle'}>
                  <Link to={'/assets/' + asset.id + '/view'}>
                    {asset.iccName} - {asset.deviceSubType?.name || 'Unknown Type'}
                  </Link>
                </GridColumn>
              </GridRow>
            ))
          ) : assetsLoaded ? (
            <GridRow columns={1}>
              <GridColumn textAlign='center' style={{ color: 'gray', fontStyle: 'italic' }}>
                No assets assigned to this member
              </GridColumn>
            </GridRow>
          ) : (
            <GridRow columns={1}>
              <GridColumn textAlign='center' style={{ color: 'gray' }}>
                Loading assets...
              </GridColumn>
            </GridRow>
          )}

          {multipleTeamsAllowed() && <Divider style={{ width: '100%' }} horizontal>
            <Header as='h4'>
              <Icon name='users' />
              Primary Teams
            </Header>
          </Divider>}

          {multipleTeamsAllowed() && formDetails.teams?.map((team) => (
            <GridRow columns={1} key={team.id}>
              <GridColumn textAlign='center' verticalAlign={'middle'}>
                <Link to={'/teams/' + team.id}>{team.name}</Link>
              </GridColumn>
            </GridRow>
          ))}

          {(multipleTeamsAllowed() || visibleSecondaryTeams.length > 0) && <>
            <Divider style={{ width: '100%' }} horizontal>
              <Header as='h4'>
                <Icon name='users' />
                Secondary Teams
              </Header>
            </Divider>
            {visibleSecondaryTeams.length === 0 ? (
              <GridRow columns={1}>
                <GridColumn textAlign='center' style={{ color: 'gray', fontStyle: 'italic' }}>
                  No secondary teams
                </GridColumn>
              </GridRow>
            ) : visibleSecondaryTeams.map((team) => (
              <GridRow columns={1} key={team.id} data-testid={`secondary-team-${team.id}`}>
                <GridColumn textAlign='center' verticalAlign='middle'>
                  <Link to={'/teams/' + team.id}>{team.name}</Link>
                  <Label size='tiny' basic color='orange' style={{ marginLeft: '0.5rem' }}>Secondary</Label>
                </GridColumn>
              </GridRow>
            ))}
          </>}

          <GridRow columns={1}>
            {canEditMemberDetails && <GridColumn textAlign={'center'}>
              <ActionButton submitText={"Save"}
                submitAction={saveChangesHandler}
                resetAction={cancelSaveChangesHandler}
                resetHide={false}
                showConfirmation
              />
            </GridColumn>}
          </GridRow>

        </Grid>
      </div>
    </LoadingWrapper>
  )
}
